// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/Reveal";
import { PageHero, PageShell } from "@/components/PageShell";
import { CardGrid } from "@/components/CardGrid";
import { LangLink as Link } from "@/components/LangLink";
import { ImageTextSection } from "@/components/ImageTextSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { Rocket, Gauge, TestTubeDiagonal } from "lucide-react";

// Images
import heroImage from "@/assets/herot8.png";
import service1 from "@/assets/t8a.png";
import service2 from "@/assets/t8b2.png";
import service3 from "@/assets/t8c2.png";

export const Route = createFileRoute("/$lang/services/t8")({
  head: () => ({
    meta: [
      { title: i18n.t("services/t8:metaTitle", "T8 Test Facility — BIZU Space") },
      {
        name: "description",
        content: i18n.t("services/t8:metaDesc",
          "Test and qualification facility for rocket engines and aerospace systems."
        ),
      },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/services/t8" }],
  }),
  component: T8Service,
});

function T8Service() {
  const { t } = useTranslation("services/t8");

  const testingCapabilities = [
    {
      title: t("cards.c1Title", "Propulsion Tests"),
      desc: t("cards.c1Desc", "Propulsion tests of liquid rocket engines from 1N to 20kN using HTP as oxidizer"),
      icon: Rocket,
    },
    {
      title: t("cards.c2Title", "Hydraulic Tests"),
      desc: t("cards.c2Desc", "Hydraulic tests for pressure vessels up to 100 bar"),
      icon: Gauge,
    },
    {
      title: t("cards.c3Title", "Combustion Tests"),
      desc: t("cards.c3Desc", "Combustion tests in controlled environment"),
      icon: TestTubeDiagonal,
    },
  ];

  return (
    <PageShell>
      <PageHero
        title={t("heroTitle", "T8 Test Facility")}
        description={t("heroDesc", "T8 is our main test facility. It is based on 3 containers.")}
        image={heroImage}
      />

      <ImageTextSection
        id="t8a"
        eyebrow={t("t8a.eyebrow", "T8A")}
        title={t("t8a.title", "Mission Control and Monitoring")}
        image={service1}
        description={
          <>
            {t("t8a.desc", "This is where we control all systems of the test through our own software CHAVES and do the monitoring of the real-time data on the same software. For this, we developed our own ")}
            <Link to="/products/daq" className="text-accent hover:underline font-medium">
              {t("t8a.linkText", "Data Acquisition system")}
            </Link>
          </>
        }
      />
      <ImageTextSection
        id="t8b"
        eyebrow={t("t8b.eyebrow", "T8B")}
        title={t("t8b.title", "Test Stand and Control Systems")}
        image={service2}
        variant="background"
        description={t("t8b.desc", "This is where all our hydraulic, pneumatic, and electronic systems used for the test are situated. Here we have a CCTV system to analyze all the test operations as well as many safety systems like firefighting, barricade protection, and others.")}
      />
      <ImageTextSection
        id="t8c"
        eyebrow={t("t8c.eyebrow", "T8C")}
        title={t("t8c.title", "HTP Concentration Laboratory")}
        image={service3}
        description={t("t8c.desc", "This is the place where we produce HTP in house from 85% to 99%. We also do quality tests in this laboratory.")}
      />
      {/* Testing Capabilities */}
      <section className="container-bizu py-24">
        <Reveal effect="scale" className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="display text-4xl md:text-5xl">{t("capabilitiesTitle", "Testing Capabilities")}</h2>
          <p className="text-muted-foreground mt-6">
            {t("capabilitiesDesc", "In T8, we are capable of performing a wide range of tests for rocket engine development, including propulsion, hydraulic, and combustion testing in controlled environments.")}
          </p>
        </Reveal>

        <CardGrid items={testingCapabilities} />

        <div className="flex justify-center mt-8">
          <Link to="/contact" className="btn-pill">
            {t("btn", "Come test your system in T8!")}
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
